import { computed, inject } from "@angular/core"
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals"
import { addEntities, setAllEntities, setEntities, withEntities } from "@ngrx/signals/entities"
import { ClinicService } from "../../services/clinic/clinic.service"
import { UserFacade } from "../user/user.facade"
import { rxMethod } from "@ngrx/signals/rxjs-interop"
import { exhaustMap, filter, pipe, tap } from "rxjs"
import { tapResponse } from "@ngrx/operators"
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper"

interface ClinicAdmin {
    id: number
    clinic_id: number
    first_name: string
    last_name: string
    email_address: string
    phone: string
    active: boolean,
    end_date: string
}

interface ClinicAdminState {
    loading: boolean
    adminListStatus: string,
    error: {
        error: boolean, message: string
    }
}

export const ClinicAdminStore = signalStore(
    {providedIn: 'root'},
    withEntities<ClinicAdmin>(),
    withState<ClinicAdminState>({
        loading: false,
        adminListStatus: 'idle',
        error: {
            error: false, message: ''
        }
    }),
    withProps((store) => ({
        clinicService: inject(ClinicService),
        userFacade: inject(UserFacade)
    })),
    withComputed((store) => ({
        adminList: computed(() => Object.values(store.entities()).filter(((admin: any) => 
            admin.clinic_id === store.userFacade.clinic_id()
        )))
    })),
    withMethods((store) => ({
        clear: function() {
            patchState(store, {
                entityMap: {},
                ids: [],
            });
        },
        clearError: function() {
            patchState(store, {
                error: {
                    error: false, message: ''
                }
            })
        },
        setError(error: boolean, message: string) {
            patchState(store, {
                error: {
                    error, message
                }
            })
        },
        setAdminListStatus(adminListStatus: string) {
            patchState(store, { adminListStatus})
        }
    })),
    withMethods((store) => ({
        getClinicAdmins: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                }),
                filter(() => !store.adminList().length),
                tap(() => {
                    store.setAdminListStatus('loading')
                }),
                exhaustMap(() => 
                    store.clinicService.getClinicAdmins(store.userFacade.clinic_id()).pipe(
                        tapResponse({
                            next: (response: any) => {

                                const admins = Array.isArray(response.admins) ? response.admins : []

                                patchState(store, setEntities(admins))
                                store.setAdminListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setAdminListStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        addClinicAdmin: rxMethod<any>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setAdminListStatus('loading')
                }),
                exhaustMap((dto) => 
                    store.clinicService.saveClinicAdmin(store.userFacade.clinic_id(), dto).pipe(
                        tapResponse({
                            next: (admin: any) => {
                                patchState(store, addEntities([admin]))
                                store.setAdminListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setAdminListStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        deleteClinicAdmin: rxMethod<any>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setAdminListStatus('loading')
                }),
                exhaustMap((admin) => 
                    store.clinicService.deleteClinicAdmin(store.userFacade.clinic_id(), admin.id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const admins = store.adminList().filter((a) => a.id !== admin.id)
                                patchState(store, setAllEntities(admins))
                                store.setAdminListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setAdminListStatus('error')
                            }
                        })
                    )
                )
            )
        )
    }))
        
)